import * as React from 'react'
import { useState } from 'react'
import { Loader } from '@/ui'
import {
  ServiceAccount,
  ServiceAccountAccessEntry,
  useMutationSetServiceAccountAccess,
  useQueryServiceAccountAccess,
} from '@/modules/shared/query-client/useServiceAccountAdmin'
import { toast } from '@/lib/toasts/api'
import { AccessByCompany } from './AccessByCompany'
import { AccessPeople } from './AccessPeople'
import { accessErrorMessage, reportResults, useRoleOptions } from './shared'
import { SubNavigationButton } from '@/modules/shared/components/SubNavigationButton'
import { extractCode } from '../ServiceAccountRoster'

type View = 'people' | 'companies'

const VIEWS: Array<{ key: View; label: string }> = [
  { key: 'people', label: 'Osoby' },
  { key: 'companies', label: 'Po firmach' },
]

// Role ludzi we wszystkich firmach konta usługowego na jednym ekranie - bez przełączania firmy
// w pickerze. Dostęp do całego konta (roster) zostaje w zakładce „Użytkownicy”.
export const ServiceAccountAccessPanel: React.FC<{ account: ServiceAccount }> = ({ account }) => {
  const [view, setView] = useState<View>('people')
  const { data, isLoading, error } = useQueryServiceAccountAccess(account.id)
  const mutation = useMutationSetServiceAccountAccess(account.id)
  const roleOptions = useRoleOptions(account.id)

  const companyName = (id: string) => data?.companies.find((c) => c.id === id)?.name ?? id

  const submit = async (entries: ServiceAccountAccessEntry[]) => {
    try {
      return await mutation.mutateAsync(entries)
    } catch (e) {
      toast.error({ text: `Nie udało się zapisać: ${accessErrorMessage(extractCode(e))}` })
      return null
    }
  }

  const save = async (entries: ServiceAccountAccessEntry[]) => {
    const results = await submit(entries)
    return !!results && reportResults(results, companyName).length === 0
  }

  if (isLoading) return <Loader fill />
  if (error || !data) {
    const status = (error as any)?.response?.status
    return (
      <div className="tw-p-4 tw-text-sm tw-text-error">
        {status === 403
          ? 'Brak uprawnień do zarządzania dostępami tego konta.'
          : 'Nie udało się załadować dostępów.'}
      </div>
    )
  }

  // Wypełnia rodzica (panel właściciela); w panelu admina rodzic nie ma wysokości, stąd min-h.
  return (
    <div className="tw-flex tw-h-full tw-min-h-[560px] tw-flex-col">
      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1 tw-border-b tw-border-base-300 tw-px-2">
        {VIEWS.map((v) => (
          <SubNavigationButton key={v.key} isActive={view === v.key} onClick={() => setView(v.key)}>
            {v.label}
          </SubNavigationButton>
        ))}
        <span className="tw-ml-auto tw-px-2 tw-text-xs tw-text-content-base tw-opacity-70">
          Firmy: {data.companies.length} · osoby: {data.users.length}
        </span>
      </div>

      {view === 'companies' && (
        <AccessByCompany
          access={data}
          roleOptions={roleOptions}
          busy={mutation.isPending}
          onSave={save}
        />
      )}
      {view === 'people' && (
        <AccessPeople
          access={data}
          accountChildren={account.children}
          roleOptions={roleOptions}
          busy={mutation.isPending}
          onSave={save}
        />
      )}
    </div>
  )
}
